// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useContext, useEffect, useState } from 'react';
import message from '../../messages';
import { Col, Expander, Row, ExpanderList, List, ListItem, Alert } from '@op/opds.opux';
import { IconNotification } from '@op/opds.opux-icons/indicator';
import PreviewList from './PreviewList';
import { ContextWrapper, AdminAgreementType, Role, AdminData } from '../../context/ContextWrapper';
import './preview.scss';
import { sortByAgreementType } from '../../util/Util';
import { breakpoint } from '@op/opds.tokens';
import { AgreementTypes } from '../../Constants';

interface PreviewValue {
    title: string;
    value: string;
}

const Preview: React.FC = () => {
    const getAdminAgreementTypes = (admin: AdminData): AdminAgreementType[] => {
        return Object.keys(admin.agreementTypes)
            .sort(sortByAgreementType)
            .map((agreementType: string) => {
                return {
                    roles: admin.agreementTypes[agreementType].rolesData.roles,
                    allRolesSelected: admin.agreementTypes[agreementType].rolesData.allRolesSelected,
                    agreementType: agreementType,
                };
            });
    };

    const getAgreementTitleWithRoleCount = (agreementType: AdminAgreementType, key: string): React.JSX.Element => {
        const selectedRoles = agreementType.roles.filter((role: Role) => role.isChecked).length;
        const totalRoles = agreementType.roles.length;
        return (
            <span className="agreement-type-span" key={key}>
                <span>{message(agreementType.agreementType as string)}</span>
                <span>
                    ({selectedRoles}/{totalRoles} {message('rolesSelected')})
                </span>
            </span>
        );
    };

    const getAdminExpanderTitle = (admin: AdminData, i: number): React.JSX.Element => {
        const countByAgreementType = (admin: AdminData): React.JSX.Element[] => {
            return getAdminAgreementTypes(admin).map((type: AdminAgreementType) => {
                return getAgreementTitleWithRoleCount(type, `role-span-${i}-${type.agreementType}`);
            });
        };

        return (
            <div className="admin-expander-title">
                <span className="admin-name-span opux-font-weight-bold">
                    {admin.firstName} {admin.lastName}
                </span>
                <span>{countByAgreementType(admin)}</span>
            </div>
        );
    };

    const { contractData, isAdminFlow } = useContext(ContextWrapper);
    const { companyData, administrators: adminsData } = contractData;

    const [isMobile, setIsMobile] = useState<boolean>(false);

    useEffect(() => {
        const widthThreshold = breakpoint.xs.max;
        const mediaQuery = window.matchMedia(`(max-width: ${widthThreshold})`);
        mediaQuery.onchange = (e: MediaQueryListEvent) => {
            setIsMobile(e.matches);
        };

        return () => {
            mediaQuery.onchange = null;
        };
    }, []);

    const adminValues = (adminData: AdminData): PreviewValue[] => {
        return [
            {
                title: message('firstName'),
                value: adminData.firstName,
            },
            {
                title: message('lastName'),
                value: adminData.lastName,
            },
            {
                title: message('personalIdentityCode'),
                value: adminData.personalIdentityCode,
            },
            {
                title: message('phoneAdmin'),
                value: adminData.adminPhoneNumber,
            },
            {
                title: message('adminEmailAddress'),
                value: adminData.adminEmailAddress,
            },
            {
                title: message('canEditRoles'),
                value: adminData.canEditRoles ? message('yes') : message('no'),
            },
        ];
    };

    const companyValues = (isForeignCompany: boolean): PreviewValue[] => {
        const generalCompanyValues: PreviewValue[] = [
            {
                title: message('businessId'),
                value: companyData.businessId,
            },
            {
                title: message('companyName'),
                value: companyData.companyName,
            },
            {
                title: message('streetAddress'),
                value: companyData.streetAddress as string,
            },
            {
                title: message('postalCode'),
                value: companyData.postalCode as string,
            },
            { title: message('cityTown'), value: companyData.cityTown as string },
            { title: message('phone'), value: companyData.phone as string },
        ];
        if (isForeignCompany) {
            return [...generalCompanyValues, { title: message('country'), value: companyData.country as string }];
        }
        return generalCompanyValues;
    };

    const companyValuesIsAdminFlow: PreviewValue[] = [
        {
            title: message('businessId'),
            value: companyData.businessId,
        },
        {
            title: message('companyName'),
            value: companyData.companyName,
        },
    ];

    const getRoleGroups = (roles: Role[]): string[] => {
        let roleList = roles.filter((e) => e.isChecked).map((item) => item.heading);
        return Array.from(new Set(roleList));
    };

    return (
        <>
            <div id="companyInformation" data-testid="companyInformation">
                <h2
                    className="opux-spacing-top-margin-double
                                           opux-spacing-bottom-margin-double"
                    data-testid="companyInformationH2"
                >
                    {message('companyInformation')}
                </h2>
                <PreviewList
                    values={
                        isAdminFlow ? companyValuesIsAdminFlow : companyValues(companyData.isForeignCompany as boolean)
                    }
                />
            </div>
            {!isAdminFlow && (
                <div>
                    <h2
                        className="opux-spacing-top-margin-double
                                           opux-spacing-bottom-margin"
                        data-testid="companyServicesH2"
                    >
                        {message('companyServices')}
                    </h2>
                    <List className="previewList" border="none" data-testid="companyServicesList">
                        {companyData.companyTypes.map((type: string) => (
                            <ListItem key={`companyType-${type}`} data-testid={`companyType-${type}`}>
                                <div className="selected-services-list-margin-left">{message(type)}</div>
                            </ListItem>
                        ))}
                    </List>
                </div>
            )}
            <div id="adminInformation" data-testid="adminInformation">
                <h2
                    className="opux-spacing-top-margin-double
                                               opux-spacing-bottom-margin-double"
                    data-testid="adminInformationH2"
                >
                    {message('adminHeader')}
                </h2>
                {adminsData.map((admin: AdminData, i: number) => (
                    <Expander
                        indented
                        key={`${admin.firstName} ${admin.lastName} ${admin.personalIdentityCode}`}
                        title={getAdminExpanderTitle(admin, i)}
                        data-testid={`adminInformation-${i}`}
                        wrapContent={!isMobile}
                    >
                        <div className="admin" data-testid={`admin-${i}`}>
                            <PreviewList values={adminValues(admin)} />
                            <Row className="opux-spacing-top-margin-double">
                                <Col id="alert-column" data-testid="alert-column">
                                    <IconNotification color="notification" />
                                    <div id="alert-text" data-testid="alertText">
                                        {message('canEditRolesInfo')}
                                    </div>
                                </Col>
                            </Row>
                            <h3 className="opux-spacing-top-margin" data-testid="accessRightsH3">
                                {message('accessRights')}
                            </h3>
                            <ExpanderList className="opux-spacing-top-margin" data-testid="accessRightsExpanderList">
                                {getAdminAgreementTypes(admin).map((agreementType: AdminAgreementType, j: number) => (
                                    <Expander
                                        indented
                                        // eslint-disable-next-line max-len
                                        key={`access-rights-admin-${admin.firstName}-${admin.lastName}-${agreementType.agreementType}`}
                                        title={getAgreementTitleWithRoleCount(agreementType, `role-expander-${i}-${j}`)}
                                        wrapContent={!isMobile}
                                        data-testid={`access-rights-expander-${i}-${j}`}
                                    >
                                        {isAdminFlow && agreementType.agreementType === AgreementTypes.COMPANY && (
                                            <Alert data-testid="adminFlowOrgUnitAlert">
                                                {message('adminFlowOrgUnitText')}
                                            </Alert>
                                        )}
                                        <div
                                            data-testid={`access-rights-list-${i}`}
                                            key={`access-rights-${agreementType.agreementType}`}
                                        >
                                            {getRoleGroups(agreementType.roles).map((heading: string) => (
                                                <div key={heading} data-testid={`role-group-${heading}`}>
                                                    <h4
                                                        className="heading-padding"
                                                        data-testid={`role-group-heading-${heading}`}
                                                    >
                                                        {message(heading)}
                                                    </h4>
                                                    <span>
                                                        {admin.agreementTypes[
                                                            agreementType.agreementType as string
                                                        ].rolesData.roles
                                                            // TODO: Refactor to not nest func more than 4 levels deep.
                                                            .filter((e: Role) => e.heading === heading)
                                                            .map((role: Role) =>
                                                                role.isChecked ? (
                                                                    <p
                                                                        key={role.name}
                                                                        className="admin-role-checkbox-margin-left
                                                                          role-text"
                                                                        data-testid={`role-${role.name}`}
                                                                    >
                                                                        {'\u2022'} {message(String(role.name))}
                                                                    </p>
                                                                ) : (
                                                                    ''
                                                                )
                                                            )}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </Expander>
                                ))}
                            </ExpanderList>
                        </div>
                    </Expander>
                ))}
            </div>
        </>
    );
};

export default Preview;
